"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import {
  BehaviorArt,
  StickerArt,
  TokenArt,
} from "@/components/tools/printables/reward-chart-page";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { weekdayNames } from "@/lib/tool-kit/printables/calendar";
import { ACCENTS, type AccentKey } from "@/lib/tool-kit/printables/certificate";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
  parseLines,
} from "@/lib/tool-kit/printables/page";
import {
  BOARD_COUNTS,
  type BoardCount,
  layoutBehavior,
  layoutSticker,
  layoutTokenBoards,
  MAX_BOXES,
  MAX_GOALS,
  MIN_BOXES,
  type RewardShape,
  STICKER_BOX_MM,
  type StickerSize,
} from "@/lib/tool-kit/printables/reward-chart";

/** English strings. A translated content file overrides these through `ui`. */
export const REWARD_CHART_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  typeLabel: "Chart",
  types: {
    sticker: "Sticker chart — a box for every sticker",
    behavior: "Behaviour chart — goals by day of the week",
    token: "Token board — fill it to earn a reward",
  },
  titles: {
    sticker: "My Sticker Chart",
    behavior: "My Weekly Goals",
    token: "My Token Board",
  },
  titleLabel: "Title",
  shapeLabel: "Shape to fill",
  shapes: { circle: "Circle", square: "Square", star: "Star", heart: "Heart" },
  accentLabel: "Colour",
  accents: {
    navy: "Navy",
    burgundy: "Burgundy",
    forest: "Forest green",
    gold: "Antique gold",
    purple: "Purple",
    black: "Black and white",
  },
  nameLine: "Name line",
  nameLabel: "Name",
  goalLine: "Reward line",
  goalLabel: "Working for",
  rewardLabel: "Reward (optional)",
  rewardHint: "Typed on the reward line. Leave it empty to write it by hand.",
  boxesLabel: "Boxes",
  boxSizeLabel: "Box size",
  boxSizes: {
    small: "Small — 15 mm (5/8 in), dot stickers",
    medium: "Medium — 20 mm (3/4 in)",
    large: "Large — 25 mm (1 in)",
  },
  finishLabel: "Reward!",
  tooManyNotice:
    "These boxes do not all fit at the size chosen, so they were made smaller. Choose fewer boxes or a bigger page orientation for larger boxes.",
  goalsLabel: "Goals — one per line",
  goalsHint: "Up to ten goals, each with a row of shapes to mark every day.",
  sampleGoals: "Follow directions\nUse kind words\nTidy my space\nTry my best",
  daysLabel: "Days",
  days: {
    "mon-fri": "Monday to Friday",
    "mon-sat": "Monday to Saturday",
    "mon-sun": "Every day, week from Monday",
    "sun-sat": "Every day, week from Sunday",
  },
  totalColumn: "Total column",
  totalHead: "Total",
  starsNeeded: "Stars needed for the reward:",
  tokensLabel: "Tokens on each board",
  boardsLabel: "Boards per page",
  boardReward: "When my board is full I get:",
  previewLabel: "Reward chart preview",
} as const;

type ChartType = keyof typeof REWARD_CHART_GENERATOR_STRINGS.types;
type DaysKey = keyof typeof REWARD_CHART_GENERATOR_STRINGS.days;

const BOX_CHOICES = [10, 12, 15, 16, 20, 24, 25, 30, 36, 40, 50, 60];
const TOKEN_CHOICES = [3, 4, 5, 6, 7, 8, 9, 10];
const DAYS_COUNT: Record<DaysKey, number> = {
  "mon-fri": 5,
  "mon-sat": 6,
  "mon-sun": 7,
  "sun-sat": 7,
};

export default function RewardChartGeneratorWidget() {
  const strings = useToolUi(REWARD_CHART_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [type, setType] = useState<ChartType>("sticker");
  const [titles, setTitles] = useState<Record<ChartType, string>>({
    sticker: strings.titles.sticker,
    behavior: strings.titles.behavior,
    token: strings.titles.token,
  });
  const [shape, setShape] = useState<RewardShape>("star");
  const [accent, setAccent] = useState<AccentKey>("navy");
  const [showName, setShowName] = useState(true);
  const [showGoal, setShowGoal] = useState(true);
  const [reward, setReward] = useState("");
  const [boxes, setBoxes] = useState("20");
  const [boxSize, setBoxSize] = useState<StickerSize>("medium");
  const [goals, setGoals] = useState<string>(strings.sampleGoals);
  const [days, setDays] = useState<DaysKey>("mon-fri");
  const [total, setTotal] = useState(true);
  const [tokens, setTokens] = useState("5");
  const [boards, setBoards] = useState<BoardCount>(2);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");

  function chooseType(next: ChartType) {
    setType(next);
    setOrientation(next === "behavior" ? "landscape" : "portrait");
  }

  const geometry = pageGeometry(paper, orientation);
  const color = ACCENTS[accent];
  const header = {
    title: titles[type].trim(),
    nameLabel: strings.nameLabel,
    goalLabel: strings.goalLabel,
    reward,
    showName,
    showGoal,
  };
  let page: React.ReactNode = null;
  let tooMany = false;
  if (type === "sticker") {
    const count = Math.min(MAX_BOXES, Math.max(MIN_BOXES, Number(boxes)));
    const layout = layoutSticker(geometry, measure, header, count, boxSize);
    tooMany = !layout.fits || layout.box < STICKER_BOX_MM[boxSize];
    page = (
      <StickerArt
        accent={color}
        finishLabel={strings.finishLabel}
        layout={layout}
        shape={shape}
      />
    );
  } else if (type === "behavior") {
    const dayNames =
      days === "sun-sat"
        ? weekdayNames(locale, 0, "short")
        : weekdayNames(locale, 1, "short").slice(0, DAYS_COUNT[days]);
    const layout = layoutBehavior(geometry, measure, {
      header,
      goals: parseLines(goals).slice(0, MAX_GOALS),
      dayNames,
      total,
      totalLabel: strings.totalHead,
      footerLabel: strings.starsNeeded,
    });
    page = <BehaviorArt accent={color} layout={layout} shape={shape} />;
  } else {
    const layout = layoutTokenBoards(
      geometry,
      measure,
      {
        title: titles.token,
        tokens: Number(tokens),
        rewardLabel: strings.boardReward,
        reward,
      },
      boards
    );
    page = <TokenArt accent={color} boards={layout} shape={shape} />;
  }

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="rc-type"
            label={strings.typeLabel}
            onChange={chooseType}
            options={(["sticker", "behavior", "token"] as const).map(
              (value) => ({
                value,
                label: strings.types[value],
              })
            )}
            value={type}
          />
          <TextField
            id="rc-title"
            label={strings.titleLabel}
            onChange={(value) =>
              setTitles((previous) => ({ ...previous, [type]: value }))
            }
            value={titles[type]}
          />
          <TextField
            hint={strings.rewardHint}
            id="rc-reward"
            label={strings.rewardLabel}
            onChange={setReward}
            value={reward}
          />
          <SelectField
            id="rc-shape"
            label={strings.shapeLabel}
            onChange={setShape}
            options={(["star", "circle", "square", "heart"] as const).map(
              (value) => ({
                value,
                label: strings.shapes[value],
              })
            )}
            value={shape}
          />
          <SelectField
            id="rc-accent"
            label={strings.accentLabel}
            onChange={setAccent}
            options={(Object.keys(ACCENTS) as AccentKey[]).map((value) => ({
              value,
              label: strings.accents[value],
            }))}
            value={accent}
          />
          {type === "sticker" ? (
            <>
              <SelectField
                id="rc-boxes"
                label={strings.boxesLabel}
                onChange={setBoxes}
                options={BOX_CHOICES.map((value) => ({
                  value: String(value),
                  label: String(value),
                }))}
                value={boxes}
              />
              <SelectField
                id="rc-box-size"
                label={strings.boxSizeLabel}
                onChange={setBoxSize}
                options={(["small", "medium", "large"] as const).map(
                  (value) => ({
                    value,
                    label: strings.boxSizes[value],
                  })
                )}
                value={boxSize}
              />
            </>
          ) : null}
          {type === "behavior" ? (
            <SelectField
              id="rc-days"
              label={strings.daysLabel}
              onChange={setDays}
              options={(Object.keys(strings.days) as DaysKey[]).map(
                (value) => ({
                  value,
                  label: strings.days[value],
                })
              )}
              value={days}
            />
          ) : null}
          {type === "token" ? (
            <>
              <SelectField
                id="rc-tokens"
                label={strings.tokensLabel}
                onChange={setTokens}
                options={TOKEN_CHOICES.map((value) => ({
                  value: String(value),
                  label: String(value),
                }))}
                value={tokens}
              />
              <SelectField
                id="rc-boards"
                label={strings.boardsLabel}
                onChange={(value) => setBoards(Number(value) as BoardCount)}
                options={BOARD_COUNTS.map((value) => ({
                  value: String(value),
                  label: String(value),
                }))}
                value={String(boards)}
              />
            </>
          ) : null}
          <PaperControls
            idPrefix="rc"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        {type === "behavior" ? (
          <TextAreaField
            hint={strings.goalsHint}
            id="rc-goals"
            label={strings.goalsLabel}
            onChange={setGoals}
            prose
            rows={5}
            value={goals}
          />
        ) : null}
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {type === "token" ? null : (
            <Checkbox
              checked={showName}
              label={strings.nameLine}
              onChange={setShowName}
            />
          )}
          {type === "token" ? null : (
            <Checkbox
              checked={showGoal}
              label={strings.goalLine}
              onChange={setShowGoal}
            />
          )}
          {type === "behavior" ? (
            <Checkbox
              checked={total}
              label={strings.totalColumn}
              onChange={setTotal}
            />
          ) : null}
        </div>
        {tooMany ? <Notice>{strings.tooManyNotice}</Notice> : null}
        <PrintBar
          fileName={`reward-chart-${type}-${paper}`}
          pageTotal={1}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={[page]}
        svgRef={svgRef}
      />
    </div>
  );
}
